import * as XLSX from 'xlsx';
import { costKindLabel, lineLabel } from './format';
import type { OperationsExportData, ReceivedAllocation } from './hooks/useOperations';

type Cell = string | number;

function eur(cents: number): number {
    return Math.round(cents) / 100;
}

function esDate(iso: string | null): string {
    if (!iso) return '';
    const day = iso.slice(0, 10);
    const [year, month, date] = day.split('-');
    if (!year || !month || !date) return iso;
    return `${date}/${month}/${year}`;
}

function invoiceType(documentType: string, taxId: string | null): string {
    if (documentType === 'credit_note') return 'R1';
    return taxId ? 'F1' : 'F2';
}

function customerKind(value: string): string {
    return value === 'business' ? 'Empresa' : 'Particular';
}

function vatPercent(baseCents: number, vatCents: number): number {
    if (baseCents === 0) return 0;
    return Math.round((Math.abs(vatCents) / Math.abs(baseCents)) * 10000) / 100;
}

function sheet(headers: string[], rows: Cell[][], numericCols: number[]): XLSX.WorkSheet {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map((header) => ({ wch: Math.min(42, Math.max(14, header.length + 2)) }));
    for (let r = 1; r <= rows.length; r += 1) {
        for (const c of numericCols) {
            const cell = ws[XLSX.utils.encode_cell({ r, c })];
            if (cell && typeof cell.v === 'number') cell.z = '#,##0.00';
        }
    }
    return ws;
}

function receivedParts(invoiceBase: number, invoiceVat: number, invoiceTotal: number, allocations: ReceivedAllocation[]) {
    if (allocations.length > 0) return allocations;
    return [{
        businessLine: '',
        costKind: '' as ReceivedAllocation['costKind'],
        taxBaseCents: invoiceBase,
        taxAmountCents: invoiceVat,
        totalCents: invoiceTotal,
    }];
}

export function downloadOperationsWorkbook(data: OperationsExportData, from: string, to: string): void {
    const issuedHeaders = [
        'Fecha expedición',
        'Tipo factura',
        'Clave régimen',
        'Serie',
        'Número',
        'NIF emisor',
        'Nombre emisor',
        'NIF/DNI cliente',
        'Nombre cliente',
        'Email',
        'Tipo cliente',
        'Dirección',
        'Código postal',
        'Población',
        'Provincia',
        'País',
        'Línea',
        'Referencia',
        'Nº línea',
        'Descripción',
        'Cantidad',
        'Base imponible',
        'Tipo IVA %',
        'Cuota IVA',
        'Suplido',
        'Total línea',
        'Es suplido',
        'Factura rectificada',
    ];
    const issuedRows: Cell[][] = data.lines.map((line) => {
        const suplido = line.isSuplido ? eur(line.lineTotalCents) : 0;
        const base = line.isSuplido ? 0 : eur(line.taxBaseCents);
        const vat = line.isSuplido ? 0 : eur(line.taxAmountCents);
        const rate = line.isSuplido ? 0 : line.taxRateBps / 100;
        return [
            esDate(line.issuedOn),
            invoiceType(line.documentType, line.customerTaxId),
            '01',
            line.series,
            line.invoiceNumber,
            line.issuerTaxId,
            line.issuerLegalName,
            line.customerTaxId ?? '',
            line.customerName,
            line.customerEmail,
            customerKind(line.customerType),
            line.addressLine ?? '',
            line.postalCode ?? '',
            line.city ?? '',
            line.province ?? '',
            line.country ?? '',
            lineLabel(line.businessLine),
            line.orderReference ?? '',
            line.lineIndex,
            line.description,
            line.quantity,
            base,
            rate,
            vat,
            suplido,
            eur(line.lineTotalCents),
            line.isSuplido ? 'Sí' : 'No',
            line.rectifiedInvoiceNumber ?? '',
        ];
    });

    const receivedHeaders = [
        'Fecha',
        'NIF proveedor',
        'Nombre proveedor',
        'Número',
        'Línea',
        'Tipo de gasto',
        'Base imponible',
        'Tipo IVA %',
        'Cuota IVA',
        'Total',
        'Notas',
    ];
    const receivedRows: Cell[][] = [...data.received]
        .sort((a, b) => a.issuedOn.localeCompare(b.issuedOn) || a.invoiceNumber.localeCompare(b.invoiceNumber))
        .flatMap((invoice) => receivedParts(
            invoice.taxBaseCents,
            invoice.taxAmountCents,
            invoice.totalCents,
            invoice.allocations,
        ).map((part) => [
            esDate(invoice.issuedOn),
            invoice.supplierTaxId ?? '',
            invoice.supplierName,
            invoice.invoiceNumber,
            part.businessLine ? lineLabel(part.businessLine) : '',
            part.costKind ? costKindLabel(part.costKind) : '',
            eur(part.taxBaseCents),
            vatPercent(part.taxBaseCents, part.taxAmountCents),
            eur(part.taxAmountCents),
            eur(part.totalCents),
            invoice.notes ?? '',
        ]));

    const disbursementHeaders = [
        'Fecha cobro',
        'Nombre',
        'NIF/DNI',
        'Email',
        'Referencia pedido',
        'Factura emitida',
        'Tasa',
        'Pagado a la administración',
        'Fecha pago administración',
        'Estado',
        'Nota',
    ];
    const disbursementRows: Cell[][] = [...data.disbursements]
        .sort((a, b) => (a.paidAt ?? '').localeCompare(b.paidAt ?? ''))
        .map((row) => [
            esDate(row.paidAt),
            row.customerName ?? '',
            row.customerTaxId ?? '',
            row.contactEmail,
            row.publicId,
            row.invoiceNumber ?? '',
            eur(row.officialFeeCents),
            row.officialFeePaidCents == null ? '' : eur(row.officialFeePaidCents),
            esDate(row.authorityPaidAt),
            row.status,
            'Suplido. No es ingreso ni gasto de AIPECES. No registrarlo como factura de venta ni de compra.',
        ]);

    const summaryHeaders = [
        'Línea',
        'Ingreso',
        'IVA repercutido',
        'Suplido',
        'Cobrado',
        'Gasto',
        'IVA soportado',
        'Margen',
    ];
    const summaryRows: Cell[][] = data.summary.map((row) => [
        lineLabel(row.businessLine),
        eur(row.incomeBaseCents),
        eur(row.vatCents),
        eur(row.suplidoCents),
        eur(row.collectedCents),
        eur(row.expenseBaseCents),
        eur(row.expenseVatCents),
        eur(row.marginCents),
    ]);

    const legend: Cell[][] = [
        ['Periodo', `${esDate(from)} – ${esDate(to)}`],
        ['Uso', 'Libro para importar en el programa de facturación. Ese programa es el que envía el registro a Verifactu. Este Excel no es el XML de la AEAT.'],
        ['Orden', 'Facturas emitidas ordenadas por fecha, número y línea. Una fila por línea de factura.'],
        ['Tipo factura', 'F1 si hay NIF/DNI del cliente. F2 si la factura es simplificada y no hay NIF. R1 si es rectificativa. Los importes de las rectificativas van en negativo.'],
        ['Clave régimen', '01, régimen general.'],
        ['Suplido', 'La tasa de licencia no es base imponible ni cuota de IVA. Va en la columna Suplido. No es ingreso de AIPECES.'],
        ['Facturas recibidas', 'Una fila por reparto: comisión Stripe, comisión Apple, compra de producto o transporte.'],
        ['Suplidos', 'Justificante de la tasa pagada a la administración. No es una factura de gasto.'],
        ['Fuera de este libro', 'Notaría, registro y gestoría no se exportan.'],
        ['Tope', data.truncated
            ? 'Este periodo supera 10.000 líneas de factura emitida. El archivo está cortado; acota las fechas y vuelve a exportar.'
            : 'El periodo cabe entero en el archivo.'],
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet(issuedHeaders, issuedRows, [20, 21, 22, 23, 24, 25]), 'Facturas emitidas');
    XLSX.utils.book_append_sheet(wb, sheet(receivedHeaders, receivedRows, [6, 7, 8, 9]), 'Facturas recibidas');
    XLSX.utils.book_append_sheet(wb, sheet(disbursementHeaders, disbursementRows, [6, 7]), 'Suplidos');
    XLSX.utils.book_append_sheet(wb, sheet(summaryHeaders, summaryRows, [1, 2, 3, 4, 5, 6, 7]), 'Resumen');
    XLSX.utils.book_append_sheet(wb, sheet(['Campo', 'Nota'], legend, []), 'Leyenda');
    downloadWorkbook(wb, `operaciones_${from}_${to}.xlsx`);
}

/** Descarga en el navegador sin writeFile (en Vite el build de SheetJS no tiene fs). */
function downloadWorkbook(wb: XLSX.WorkBook, filename: string): void {
    const bytes = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
    const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
